import { EngagementTaskTrait } from '@cibo/core'
import { AutoFields, CiboAutoForm } from '@cibo/ui'
import AutoField from '@cibo/ui/src/components/Form/Uniforms/AutoField'
import { Stack, StackProps } from '@mui/material'
import { SubmitField } from 'dynamic-uniforms'
import { ComponentProps, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CiboAutoFormDebugger } from './DataEntrySchemaEditingProvider/CiboAutoFormDebugger'
import { componentDetector } from './DataEntrySchemaEditingProvider/componentDetector'
import { PrefillEqipContractButton } from './PrefillEqipContractButton'

type SingleTargetSchemaEditorProps = {
  traitStatus?: EngagementTaskTrait
  handleSaveTraitDetail: (detail: { value: any }) => void | Promise<any>
  traitDetailValue: any
  variant?: 'compact' | 'comfortable'
  DeleteButton?: ReactNode
  submitButtonProps?: Omit<ComponentProps<typeof SubmitField>, 'name'>
  fieldWrapperProps?: StackProps
  buttonWrapperProps?: StackProps
  onSuccess?: () => void
}

const DisplayComponents: Record<any, any> = {
  Stack,
  PrefillEqipContractButton,
}

export const SingleTargetSchemaEditor = ({
  traitStatus,
  handleSaveTraitDetail,
  traitDetailValue,
  variant,
  DeleteButton,
  submitButtonProps,
  fieldWrapperProps,
  buttonWrapperProps,
  onSuccess,
}: SingleTargetSchemaEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')
  const isCompact = variant === 'compact'

  const [model, setModel] = useState<{ value: any }>()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (traitDetailValue) {
      setModel({ value: traitDetailValue })
    }
  }, [traitDetailValue])

  const handleSubmit = useCallback(
    (model: any) => {
      setIsLoading(true)
      const result = handleSaveTraitDetail(model)
      if (result) {
        return Promise.resolve(result)
          .then(() => {
            onSuccess?.()
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
      onSuccess?.()
      setIsLoading(false)
    },
    [handleSaveTraitDetail, onSuccess]
  )

  if (!traitStatus?.schema) {
    return null
  }

  const uiParams = traitStatus.schema['ui-params'] || {}

  const renderDisplay = ({ field, itemProps }: { field: any; itemProps?: any }) => {
    if (typeof field === 'string') {
      return <AutoField key={field} name={field} {...itemProps} />
    }

    const Component = DisplayComponents[field.component]

    if (Component) {
      return (
        <Component key={field.name} {...itemProps} {...field.props}>
          {field.children?.map((child: any) => renderDisplay({ field: child }))}
        </Component>
      )
    }

    return <>{JSON.stringify(field)}</>
  }

  return (
    <AutoField.componentDetectorContext.Provider value={componentDetector}>
      <CiboAutoForm
        schema={traitStatus.schema}
        model={model}
        onSubmit={handleSubmit}
        onChangeModel={setModel}
        validate="onChange"
        warningSchema={
          uiParams?.warningSchema && {
            type: 'object',
            properties: { value: uiParams?.warningSchema },
          }
        }
        renderDisplay={renderDisplay}
      >
        {/* @ts-ignore */}
        <Stack spacing={isCompact ? 1 : 2} {...fieldWrapperProps}>
          <AutoFields />
        </Stack>
        {/* @ts-ignore */}
        <Stack {...buttonWrapperProps}>
          <SubmitField
            label={t('save')}
            data-testid={`submit-button-${traitStatus?.traitId}`}
            loading={isLoading}
            {...submitButtonProps}
          />
          {DeleteButton}
        </Stack>
        <CiboAutoFormDebugger />
      </CiboAutoForm>
    </AutoField.componentDetectorContext.Provider>
  )
}
